import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheck, FaCalendarAlt, FaSpinner, FaBan } from 'react-icons/fa';
import { TrainingRequest, TrainingInstitute } from '../types';
import axiosInstance from '../../../libs/axios';

interface RequestDetailOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  request: TrainingRequest | null;
  onHrVerify: (id: number, data: any) => void;
  onSchedule: (id: number, data: any) => void;
  institutes: TrainingInstitute[];
  loading?: boolean;
}

export const RequestDetailOffcanvas: React.FC<RequestDetailOffcanvasProps> = ({ isOpen, onClose, request, onHrVerify, onSchedule, institutes, loading = false }) => {
  const [salaryOffered, setSalaryOffered] = useState("");
  const [alternatePhone, setAlternatePhone] = useState("");
  const [rejectionRemarks, setRejectionRemarks] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [plannedLocation, setPlannedLocation] = useState("");
  const [instituteId, setInstituteId] = useState("");
  const [centerId, setCenterId] = useState("");
  const [centers, setCenters] = useState<{ id: number; center_name: string }[]>([]);

  useEffect(() => {
    if (!instituteId) { setCenters([]); setCenterId(""); return; }
    axiosInstance.get(`/training/institutes/${instituteId}/centers`)
      .then(res => setCenters(res.data?.data?.centers || []))
      .catch(() => setCenters([]));
    setCenterId("");
  }, [instituteId]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const handleHrDecision = (approved: boolean) => {
    if (!request) return;
    const data: any = {};
    data.is_approved = approved;
    if (approved) {
      if (salaryOffered) data.salary_offered = Number(salaryOffered);
      if (alternatePhone) data.candidate_alternate_phone = alternatePhone;
    } else {
      if (rejectionRemarks.trim()) data.rejection_remarks = rejectionRemarks.trim();
    }
    onHrVerify(request.id, data);
  };

  const handleSchedule = () => {
    if (!request) return;
    const data: any = {};
    if (plannedDate) data.planned_training_date = plannedDate;
    if (plannedLocation) data.planned_location = plannedLocation;
    if (instituteId) data.training_institute_id = Number(instituteId);
    if (centerId) data.institute_center_id = Number(centerId);
    onSchedule(request.id, data);
  };

  if (!isOpen || !request) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Request Details</h2>
            <p className="text-sm text-gray-600 mt-1">{request.request_id}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Candidate Info */}
          <div className="bg-gray-50 border border-gray-200 rounded p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Candidate Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Name:</span><p className="font-medium">{request.candidate_name}</p></div>
              <div><span className="text-gray-500">Phone:</span><p className="font-medium">{request.candidate_phone}</p></div>
              <div><span className="text-gray-500">Location:</span><p className="font-medium">{request.candidate_location}</p></div>
              <div><span className="text-gray-500">Designation:</span><p className="font-medium">{request.candidate_designation}</p></div>
              <div><span className="text-gray-500">Association:</span><p className="font-medium">{(request.association_type || '').toLowerCase().replace(/-/g, '') === 'onrole' ? 'On-Role' : 'Off-Role'}</p></div>
              <div><span className="text-gray-500">Training Type:</span><p className="font-medium">{request.training_type?.training_name || "-"}</p></div>
              <div><span className="text-gray-500">Status:</span><p className="font-medium capitalize">{request.status.replace('_', ' ')}</p></div>
              <div><span className="text-gray-500">Created:</span><p className="font-medium">{formatDate(request.created_at)}</p></div>
            </div>
          </div>

          {/* Deduction Info */}
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Deduction Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-blue-600">Applicable:</span><p className="font-medium">{request.deduction_applicable ? "Yes" : "No"}</p></div>
              {request.deduction_applicable && (
                <>
                  <div><span className="text-blue-600">Period:</span><p className="font-medium capitalize">{request.deduction_period?.replace('_', ' ') || "-"}</p></div>
                  <div className="col-span-2"><span className="text-blue-600">Remarks:</span><p className="font-medium">{request.deduction_remarks || "-"}</p></div>
                </>
              )}
            </div>
          </div>

          {/* Scheduling Info (if scheduled) */}
          {request.planned_training_date && (
            <div className="bg-purple-50 border border-purple-200 rounded p-4">
              <h3 className="text-sm font-semibold text-purple-800 mb-2">Scheduling Details</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-purple-600">Planned Date:</span><p className="font-medium">{formatDate(request.planned_training_date)}</p></div>
                <div><span className="text-purple-600">Location:</span><p className="font-medium">{request.planned_location || "-"}</p></div>
                <div><span className="text-purple-600">Institute:</span><p className="font-medium">{request.training_institute?.institute_name || "-"}</p></div>
              </div>
            </div>
          )}

          {/* HR Verify Action */}
          {request.status === 'pending' && (
            <div className="border border-green-200 rounded p-4 bg-green-50">
              <h3 className="text-sm font-semibold text-green-800 mb-3">HR Verification</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Offered</label>
                  <input type="number" min={0} value={salaryOffered} onChange={(e) => setSalaryOffered(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 text-sm" placeholder="e.g., 35000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                  <input type="text" value={alternatePhone} onChange={(e) => setAlternatePhone(e.target.value)} maxLength={10} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 text-sm" placeholder="Alternate phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Remarks (required if rejecting)</label>
                  <textarea
                    value={rejectionRemarks}
                    onChange={(e) => setRejectionRemarks(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-red-500 text-sm resize-none"
                    rows={3}
                    placeholder="Reason for rejection"
                    disabled={loading}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button onClick={() => handleHrDecision(true)} disabled={loading} className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaCheck className="w-4 h-4" />}
                    Approve
                  </button>
                  <button onClick={() => handleHrDecision(false)} disabled={loading || !rejectionRemarks.trim()} className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaBan className="w-4 h-4" />}
                    Reject
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Schedule Action */}
          {request.status === 'hr_verified' && (
            <div className="border border-purple-200 rounded p-4 bg-purple-50">
              <h3 className="text-sm font-semibold text-purple-800 mb-3">Schedule Training</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Training Date</label>
                  <input type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input type="text" value={plannedLocation} onChange={(e) => setPlannedLocation(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm" placeholder="Training location" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institute</label>
                  <select value={instituteId} onChange={(e) => setInstituteId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm bg-white">
                    <option value="">Select institute</option>
                    {institutes.map(i => <option key={i.id} value={i.id}>{i.institute_name}</option>)}
                  </select>
                </div>
                {centers.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Center <span className="text-gray-400 font-normal">(optional)</span></label>
                    <select value={centerId} onChange={(e) => setCenterId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm bg-white">
                      <option value="">Select center</option>
                      {centers.map(c => <option key={c.id} value={c.id}>{c.center_name}</option>)}
                    </select>
                  </div>
                )}
                <button onClick={handleSchedule} disabled={loading || !plannedDate} className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaCalendarAlt className="w-4 h-4" />}
                  Schedule Training
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
